const canHover = () =>
    window.matchMedia('(hover: hover) and (pointer: fine)').matches;

function initInvestmentProfile() {
    const blocks = document.querySelectorAll('.investment-profile');
    if (!blocks.length) return;

    const controllers = [];

    blocks.forEach((block) => {
        const hotspots = block.querySelectorAll('.investment-profile__hotspot');
        const tooltips = block.querySelectorAll('.investment-profile__tooltip');

        const closeAll = () => {
            tooltips.forEach((t) => t.classList.remove('is-visible'));
            hotspots.forEach((h) => h.setAttribute('aria-expanded', 'false'));
        };

        const open = (hotspot, tooltip) => {
            closeAll();
            tooltip.classList.add('is-visible');
            hotspot.setAttribute('aria-expanded', 'true');
            fitTooltip(tooltip);
        };

        hotspots.forEach((hotspot) => {
            const id = hotspot.getAttribute('aria-controls');
            const tooltip = id ? block.querySelector('#' + id) : null;
            if (!tooltip) return;

            hotspot.addEventListener('mouseenter', () => {
                if (canHover()) open(hotspot, tooltip);
            });

            hotspot.addEventListener('mouseleave', () => {
                if (canHover()) closeAll();
            });

            hotspot.addEventListener('click', (e) => {
                if (!canHover()) {
                    e.stopPropagation();
                    const wasOpen = tooltip.classList.contains('is-visible');
                    if (wasOpen) {
                        closeAll();
                    } else {
                        open(hotspot, tooltip);
                    }
                }
            });
        });

        controllers.push(closeAll);

        // Close the open tooltip as soon as the graphic is scrolled.
        const graphic = block.querySelector('.investment-profile__graphic');
        if (graphic) {
            graphic.addEventListener('scroll', closeAll, { passive: true });
        }

        initScrollbar(block);
    });

    document.addEventListener('click', (e) => {
        if (canHover()) return;
        if (
            e.target.closest('.investment-profile__hotspot') ||
            e.target.closest('.investment-profile__tooltip')
        ) {
            return;
        }
        controllers.forEach((closeAll) => closeAll());
    });
}

function fitTooltip(tooltip) {
    const body = tooltip.querySelector('.investment-profile__tooltip-body');
    if (!body) return;

    const margin = 16;
    // Keep the arrow (anchored at the circle) clear of the body's rounded corners.
    const arrowInset = 12;

    body.style.setProperty('--tooltip-shift', '0px');

    const rect = body.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    let shift = 0;

    if (rect.left < margin) {
        shift = margin - rect.left;
    } else if (rect.right > viewportWidth - margin) {
        shift = viewportWidth - margin - rect.right;
    }

    // Never shift so far that the arrow leaves the body — connection wins over fit.
    const maxShift = Math.max(rect.width / 2 - arrowInset, 0);
    shift = Math.max(-maxShift, Math.min(maxShift, shift));

    body.style.setProperty('--tooltip-shift', `${shift}px`);
}

function initScrollbar(block) {
    const graphic = block.querySelector('.investment-profile__graphic');
    const track = block.querySelector('.investment-profile__scrollbar');
    const thumb = block.querySelector('.investment-profile__scrollbar-thumb');

    if (!graphic || !track || !thumb) return;

    const updateThumb = () => {
        const { scrollWidth, clientWidth, scrollLeft } = graphic;
        const trackWidth = track.clientWidth;
        const overflow = scrollWidth - clientWidth;

        if (overflow <= 0 || trackWidth <= 0) {
            thumb.style.width = '0';
            return;
        }

        const thumbWidth = Math.max((clientWidth / scrollWidth) * trackWidth, 24);
        const maxThumbLeft = trackWidth - thumbWidth;
        const thumbLeft = (scrollLeft / overflow) * maxThumbLeft;

        thumb.style.width = `${thumbWidth}px`;
        thumb.style.transform = `translateX(${thumbLeft}px)`;
    };

    let resizeTimer;
    const updateThumbDebounced = () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(updateThumb, 100);
    };

    graphic.addEventListener('scroll', updateThumb, { passive: true });
    window.addEventListener('resize', updateThumbDebounced);
    window.addEventListener('load', updateThumb);

    let dragging = false;
    let dragStartX = 0;
    let dragStartScrollLeft = 0;
    let dragMaxThumbLeft = 0;
    let dragOverflow = 0;

    thumb.addEventListener('pointerdown', (e) => {
        dragging = true;
        dragStartX = e.clientX;
        dragStartScrollLeft = graphic.scrollLeft;
        dragMaxThumbLeft = track.clientWidth - thumb.offsetWidth;
        dragOverflow = graphic.scrollWidth - graphic.clientWidth;
        if (thumb.setPointerCapture) thumb.setPointerCapture(e.pointerId);
        e.preventDefault();
    });

    thumb.addEventListener('pointermove', (e) => {
        if (!dragging || dragMaxThumbLeft <= 0) return;
        const delta = e.clientX - dragStartX;
        graphic.scrollLeft = dragStartScrollLeft + (delta / dragMaxThumbLeft) * dragOverflow;
    });

    const endDrag = (e) => {
        if (!dragging) return;
        dragging = false;
        if (thumb.releasePointerCapture && e.pointerId != null) {
            thumb.releasePointerCapture(e.pointerId);
        }
    };

    thumb.addEventListener('pointerup', endDrag);
    thumb.addEventListener('pointercancel', endDrag);

    updateThumb();
}

document.addEventListener('DOMContentLoaded', initInvestmentProfile);
